<template>
  <div>
    <!-- Filters -->
    <b-card no-body>
      <b-card-body v-if="!confirmStep">
        <b-row>
          <b-col cols="12" md="6" class="mb-2">
            <b-form-group label="Consultor de origem" label-for="oldConsultant">
              <v-select
                id="oldConsultant"
                v-model="oldConsultant"
                :reduce="(all_consultants) => all_consultants.id"
                :options="allConsultants"
                :loading="loading.allConsultants"
                label="name"
                @input="search"
                :class="getSelectErrorClass(v$.oldConsultant.$error)"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
              <div class="invalid-feedback">
                <span v-if="v$.oldConsultant.required.$invalid">
                  Você deve informar um consultor de origem
                </span>
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6" class="mb-2">
            <b-form-group
              label="Consultor de destino"
              label-for="newConsultant"
            >
              <v-select
                id="newConsultant"
                v-model="newConsultant"
                :reduce="(consultant_list) => consultant_list.id"
                :options="consultants"
                :loading="loading.consultants"
                label="name"
                :class="getSelectErrorClass(v$.newConsultant.$error)"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
              <div class="invalid-feedback">
                <span v-if="v$.newConsultant.required.$invalid">
                  Você deve informar um consultor de destino
                </span>
                <span v-else-if="v$.newConsultant.notEqual.$invalid">
                  O consultor de destino não pode ser o mesmo de origem
                </span>
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="12" class="mb-2">
            <b-form-group label="Leads para transferência">
              <b-table
                hover
                class="position-relative"
                :items="leadsList"
                responsive
                :fields="tableColumns"
                primary-key="id"
                show-empty
                empty-text="Não foram encontrados leads para transferência"
                :busy="loading.leads"
                @row-clicked="selectLead"
              >
                <template #head(selection)="data">
                  <b-form-checkbox
                    v-model="selectAllLeads"
                    @input="selectAll"
                  />
                </template>
                <!-- Column: selection -->
                <template #cell(selection)="data">
                  <div class="text-wrap">
                    <b-form-checkbox
                      v-model="selectedLeads"
                      :value="data.item.id"
                      @click.native.stop
                    />
                  </div>
                </template>

                <!-- Column: Name -->
                <template #cell(name)="data">
                  <div class="text-wrap">
                    <span class="align-text-top text-capitalize">{{
                      data.item.name
                    }}</span>
                  </div>
                </template>

                <!-- Column: income -->
                <template #cell(income)="data">
                  <div class="text-wrap">
                    <span
                      class="align-text-top text-capitalize"
                      v-if="
                        data.item.customer_additionals &&
                        data.item.customer_additionals.income
                      "
                    >
                      {{
                        (data.item.income =
                          data.item.customer_additionals.income.name)
                      }}
                    </span>
                  </div>
                </template>

                <!-- Column: monthly savings -->
                <template #cell(monthly_savings)="data">
                  <div class="text-wrap">
                    <span
                      class="align-text-top text-capitalize"
                      v-if="
                        data.item.customer_additionals &&
                        data.item.customer_additionals.monthly_savings
                      "
                    >
                      {{
                        (data.item.monthly_savings =
                          data.item.customer_additionals.monthly_savings
                          | toCurrency)
                      }}
                    </span>
                  </div>
                </template>

                <!-- Column: goal -->
                <template #cell(goal)="data">
                  <div class="text-wrap">
                    <span class="align-text-top text-capitalize">{{
                      data.item.goal | leadGoal
                    }}</span>
                  </div>
                </template>

                <!-- Column: income -->
                <template #cell(status)="data">
                  <div class="text-wrap">
                    <span
                      class="align-text-top text-capitalize"
                      v-if="data.item.customer_status"
                    >
                      {{ (data.item.status = data.item.customer_status.name) }}
                    </span>
                  </div>
                </template>
              </b-table>
              <div
                class="invalid-feedback"
                style="display: block"
                v-if="v$.selectedLeads.$error"
              >
                <span> Você deve selecionar ao menos um lead </span>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="d-flex align-items-center justify-content-end mb-2">
          <div class="mx-1" cols="12" md="6">
            <b-button variant="primary" @click="confirm">
              Transferir leads
            </b-button>
          </div>
        </b-row>
      </b-card-body>
      <b-card-body v-else>
        <b-row class="m-2">
          <b-col cols="12" md="6">
            <small class="text-muted"> Consultor de origem </small>
            <h5>{{ oldConsultantName }}</h5>
          </b-col>
          <b-col cols="12" md="6">
            <small class="text-muted"> Consultor de destino </small>
            <h5>{{ newConsultantName }}</h5>
          </b-col>
        </b-row>
        <b-row class="mt-2 ml-2">
          <b-col cols="12">
            <div class="d-flex justify-content-start">
              <h5>Leads</h5>
            </div>
          </b-col>
        </b-row>
        <b-row class="mb-2 ml-2">
          <b-col cols="12" md="6" class="d-inline-block">
            <template v-for="(lead, index) in leads">
              <div v-if="index < leads.length / 2" :key="`lead-${lead.id}`">
                <span class="d-inline-block">{{ lead.name }}</span>
                <br /><br />
              </div>
            </template>
          </b-col>
          <b-col cols="12" md="6" class="d-inline-block">
            <template v-for="(lead, index) in leads">
              <div v-if="index >= leads.length / 2" :key="`lead-${lead.id}`">
                <span class="d-inline-block">{{ lead.name }}</span>
                <br /><br />
              </div>
            </template>
          </b-col>
        </b-row>

        <b-row class="d-flex align-items-center justify-content-end m-2">
          <div class="mx-1" cols="12" md="6">
            <b-button
              variant="outline-secondary"
              class="mr-1"
              @click="confirmStep = false"
              :disabled="saving"
            >
              Cancelar
            </b-button>
            <b-button variant="primary" @click="save" :disabled="saving">
              <b-spinner v-if="saving" small />
              <feather-icon v-else icon="SaveIcon" class="mr-50" />
              <span>
                {{ saving ? "Salvando..." : "Salvar" }}
              </span>
            </b-button>
          </div>
        </b-row>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import _ from "lodash";
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
  BFormGroup,
  BFormCheckbox,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters, mapMutations } from "vuex";
import useVuelidate from "@vuelidate/core";
import { required, minLength } from "@vuelidate/validators";
import { getVueSelectErrorClass } from "@/helpers/validators";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as sharedTypes from "@/modules/shared/store/types";
import * as types from "../store/types";
import { LEAD } from "@/constants/customers_types";

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    BFormGroup,
    BFormCheckbox,
    vSelect,
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  data() {
    return {
      loading: {
        allConsultants: false,
        consultants: false,
        leads: false,
      },
      tableColumns: [
        { key: "selection", label: "", thStyle: "width: 50px" },
        { key: "name", label: "Nome", stickyColumn: true },
        { key: "income", label: "Renda", sortable: true },
        { key: "monthly_savings", label: "Poupa", sortable: true },
        { key: "goal", label: "Objetivo", sortable: true },
        { key: "status", label: "Status", sortable: true },
      ],
      confirmStep: false,
      saving: false,
      oldConsultant: undefined,
      newConsultant: undefined,
      selectAllLeads: false,
      selectedLeads: [],
    };
  },
  validations() {
    return {
      oldConsultant: { required },
      newConsultant: {
        required,
        notEqual: (consultant) => {
          return this.oldConsultant !== consultant;
        },
      },
      selectedLeads: {
        required,
        minLength: minLength(1),
      },
    };
  },
  computed: {
    ...mapGetters({
      leadsList: sharedTypes.CUSTOMERS,
      allConsultants: sharedTypes.ALL_CONSULTANTS,
      consultants: sharedTypes.CONSULTANTS,
    }),
    leads: function () {
      const leads = _.filter(this.leadsList, (val) =>
        this.selectedLeads.includes(val.id)
      );
      return leads;
    },
    oldConsultantName: function () {
      const consultant = _.find(
        this.allConsultants,
        (val) => this.oldConsultant == val.id
      );
      return consultant.name;
    },
    newConsultantName: function () {
      const consultant = _.find(
        this.consultants,
        (val) => this.newConsultant == val.id
      );
      return consultant.name;
    },
  },
  mounted() {
    this.search();
    this.loading.allConsultants = true;
    this.getAllConsultants()
      .catch((error) => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.allConsultants = false;
      });
    this.loading.consultants = true;
    this.getConsultants()
      .catch((error) => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.consultants = false;
      });
  },
  methods: {
    ...mapActions({
      getLeads: sharedTypes.GET_ALL_CUSTOMERS,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getAllConsultants: sharedTypes.GET_ALL_CONSULTANTS,
      transferLeads: types.TRANSFER_LEADS,
    }),
    ...mapMutations({
      mutateLeadList: sharedTypes.MUTATE_CUSTOMERS,
    }),
    search() {
      if (this.oldConsultant) {
        this.selectedLeads = [];
        this.selectAllLeads = false;
        this.loading.leads = true;
        this.getLeads({
          customer_type: LEAD,
          consultant_id: this.oldConsultant,
        })
          .catch((error) => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao buscar os leads. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          })
          .finally(() => {
            this.loading.leads = false;
          });
      } else {
        this.mutateLeadList([]);
      }
    },
    async confirm() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      this.confirmStep = true;
    },
    async save() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      this.saving = true;
      this.transferLeads({
        old_consultant_id: this.oldConsultant,
        new_consultant_id: this.newConsultant,
        leads: this.selectedLeads,
      })
        .then((resp) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso!",
              text: "Transferência realizada com sucesso.",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao realizar a transferência. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.clear();
          this.saving = false;
        });
    },
    selectAll() {
      if (this.selectAllLeads) {
        this.selectedLeads = _.map(this.leadsList, "id");
      } else {
        this.selectedLeads = [];
      }
    },
    selectLead(el) {
      if (!this.selectedLeads.includes(el.id)) {
        this.selectedLeads.push(el.id);
      } else {
        this.selectedLeads = _.filter(this.selectedLeads, (e) => {
          return e != el.id;
        });
      }
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },
    clear() {
      this.confirmStep = false;
      this.saving = false;
      this.oldConsultant = undefined;
      this.newConsultant = undefined;
      this.selectAllLeads = false;
      this.selectedLeads = [];
      this.mutateLeadList([]);
      this.v$.$reset();
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
